import React, { Component } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { translate, getLanguage, setLanguage } from 'react-switch-lang';
import { detectMarathiInUrl, buildDeepLink, shareDeepLink } from './utils/deepLink';
import NewHeader from './newHeader.js';
import Footer from './Footer.js';
import { toast } from 'react-toastify';
import './assets/dist/css/bootstrap.css';
import './assets/css/style.css';
import './assets/fontawesome/css/all.min.css';
import './EventsCalendarPage.css';
import * as myConstClass from './constant.js';
import {
    Card,
    Row,
    Col,
} from 'reactstrap';

// Import reusable components
import EventFilters from './components/eventListing/EventFilters';
import EventsCalendarFilters from './components/eventListing/EventsCalendarFilters';
import EventListingHero from './components/eventListing/EventListingHero';
import CalendarView from './components/eventListing/CalendarView';

// Determine category tag color
const getEventCategoryColor = (topic = '') => {
    if (!topic) return 'blue';

    const sourceText = topic.toLowerCase();

    // Newly Wed
    if (sourceText.includes('newly wed') || sourceText.includes('newlywed')) {
        return 'blue';
    }

    // Kids / Birth up to 7
    if (
        sourceText.includes('birth') ||
        sourceText.includes('upto') ||
        sourceText.includes('up to') ||
        sourceText.includes('7 years')
    ) {
        return 'pink';
    }

    // Life / Stress / Meditation
    if (
        sourceText.includes('life after death') ||
        sourceText.includes('stress') ||
        sourceText.includes('meditation') ||
        sourceText.includes('yoga')
    ) {
        return 'yellow';
    }

    // Science / Health / Spirituality
    if (
        sourceText.includes('science') ||
        sourceText.includes('spirituality') ||
        sourceText.includes('health')
    ) {
        return 'purple';
    }

    // 15–28 age group
    if (
        sourceText.includes('15') &&
        sourceText.includes('28')
    ) {
        return 'green';
    }

    return 'blue'; // fallback
};

class EventsCalendarPage extends Component {
    constructor(props) {
        super(props);
        
        // Parse URL parameters BEFORE setting initial state (like EventListingPage)
        // This ensures the filters component receives the correct initial filters on first render
        const { categoryName, categoryId } = props.match.params || {};
        const decodedCategoryName = categoryName ? decodeURIComponent(categoryName) : '';
        
        // Parse query params for shared filters
        const searchParams = new URLSearchParams(window.location.search);
        
        // Support both old (name-based) and new (ID-based) URL parameters
        const sharedEventType = searchParams.get('eventType'); // Old format (name)
        const sharedEventTypeId = searchParams.get('event_type_id'); // New format (ID)
        const sharedTopics = searchParams.get('topics'); // Old format (names)
        const sharedTopicId = searchParams.get('topic_id'); // New format (IDs)
        const sharedLanguage = searchParams.get('language');
        const sharedLanguageId = searchParams.get('language_id'); // New format (ID)
        const sharedSubTopics = searchParams.get('subTopics'); // Old format (names)
        const sharedSubtopicId = searchParams.get('subtopic_id'); // New format (IDs)
        const sharedMonth = searchParams.get('month');
        const sharedCity = searchParams.get('city'); // Old parameter name
        const sharedLocation = searchParams.get('location'); // New parameter name
        const sharedView = searchParams.get('view');
        const sharedDuration = searchParams.get('duration');
        
        // Get location state if available
        const locationState = props.location && props.location.state ? props.location.state : {};
        
        // Determine initial event type - Priority: query param ID > path params > query param name > location state > default
        let initialEventType = 'Residential Study Courses';
        
        // Store pending ID resolution for after filterOptions are loaded (on instance, not props)
        this.pendingIdResolve = {
            eventTypeId: sharedEventTypeId,
            topicIds: sharedTopicId ? sharedTopicId.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id)) : [],
            subtopicIds: sharedSubtopicId ? sharedSubtopicId.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id)) : []
        };
        
        // IMPORTANT: If event_type_id query parameter exists, wait for resolution instead of using path parameter
        // This ensures the ID takes priority over the path parameter name
        const shouldWaitForResolution = this.pendingIdResolve && this.pendingIdResolve.eventTypeId;
        
        if (!shouldWaitForResolution) {
            // No ID to resolve, use path params or other sources
            if (decodedCategoryName) {
                initialEventType = decodedCategoryName;
            } else if (sharedEventType) {
                initialEventType = sharedEventType;
            } else if (locationState.events && Array.isArray(locationState.events) && locationState.events.length > 0) {
                initialEventType = locationState.events[0];
            }
        } else {
            // Wait for ID resolution - don't set event type yet
            initialEventType = null;
        }
        
        // Parse other filters
        let topicsFilter = ['All'];
        if (sharedTopics) {
            topicsFilter = sharedTopics.split(',').map(s => s.trim()).filter(s => s !== '');
        } else if (locationState.topics) {
            topicsFilter = Array.isArray(locationState.topics) ? locationState.topics : [locationState.topics];
        }
        
        let languageFilter = 'All';
        if (sharedLanguageId) {
            // Map language_id to language name
            if (sharedLanguageId === '1') languageFilter = 'English';
            else if (sharedLanguageId === '2') languageFilter = 'Marathi';
            else if (sharedLanguageId === '3') languageFilter = 'Hindi';
            else languageFilter = 'All';
        } else if (sharedLanguage) {
            languageFilter = sharedLanguage;
        } else if (locationState.language) {
            languageFilter = locationState.language;
        }
        
        let subTopicsFilter = ['All'];
        if (sharedSubTopics) {
            subTopicsFilter = sharedSubTopics.split(',').map(s => s.trim()).filter(s => s !== '');
        } else if (locationState.subTopics) {
            subTopicsFilter = Array.isArray(locationState.subTopics) ? locationState.subTopics : [locationState.subTopics];
        }

        // Month filter
        let monthFilter = 'All';
        if (sharedMonth) {
            monthFilter = sharedMonth;
        } else if (locationState.month) {
            monthFilter = locationState.month;
        }

        // City / Location filter - use 'Select' as UI default when not provided
        let cityFilter = 'Select';
        if (sharedLocation) {
            // New parameter name
            cityFilter = sharedLocation;
        } else if (sharedCity) {
            // Old parameter name (backward compatibility)
            cityFilter = sharedCity;
        } else if (locationState.city) {
            cityFilter = locationState.city;
        }

        // Duration filter
        let durationFilter = 'All';
        if (sharedDuration) {
            durationFilter = sharedDuration;
        } else if (locationState.duration) {
            durationFilter = locationState.duration;
        }
        
        // Determine initial calendar month/year from monthFilter if provided
        let initialCurrentMonth = new Date().getMonth();
        let initialCurrentYear = new Date().getFullYear();
        if (monthFilter && monthFilter !== 'All') {
            const parts = String(monthFilter).split('-');
            if (parts.length >= 2) {
                const y = parseInt(parts[0], 10);
                const m = parseInt(parts[1], 10) - 1;
                if (!isNaN(y) && !isNaN(m)) {
                    initialCurrentYear = y;
                    initialCurrentMonth = m;
                }
            }
        }

        const initialViewMode = sharedView === 'calendar' || (locationState && locationState.viewMode === 'calendar') ? 'calendar' : 'table';

        this.state = {
            categoryName: initialEventType || decodedCategoryName || '',
            categoryId: categoryId || '',
            events: [],
            loading: true,
            viewSwitching: false, // Loading state for view transitions
            filters: {
                events: shouldWaitForResolution ? [] : (initialEventType ? [initialEventType] : ['Residential Study Courses']), // Wait for ID resolution if event_type_id is present
                topics: topicsFilter,
                language: languageFilter,
                subTopics: subTopicsFilter,
                month: monthFilter, // Month filter
                city: cityFilter, // City/Location filter (UI value)
                duration: durationFilter,// All | 1 Day | 2-3 Days | 4+ Days
                eventStatus: 'All'   // All | Active
            },
            filterOptions: {
                eventTypes: [],
                topics: [],
                ageGroups: [],
                months: [], // Store months from API
                cities: [] // Store cities from API
            },
            // viewMode: initialViewMode, // 'table' or 'calendar'
            viewMode: initialViewMode,
            currentMonth: initialCurrentMonth, // 0-11
            currentYear: initialCurrentYear,
            locationPopup: {
                show: false,
                address: '',
                googleMapsUrl: '',
                position: { top: 0, left: 0, arrowLeft: 15 }
            },
            eligibilityPopup: {
                show: false,
                event: null,
                position: { top: 0, left: 0 }
            },
            infoPopup: {
                show: false,
                event: null,
                position: { top: 0, left: 0 }
            },
            topicTooltip: {
                show: false,
                eventId: null,
                text: '',
                position: { top: 0, left: 0 }
            },
            eventDetailsCache: {},
            infoLoading: false
            ,
            mobileModal: {
                show: false,
                type: '', // 'location' | 'info' | 'eligibility'
                event: null
            }
        };
    }

    // Timer for location popup close delay
    locationPopupTimer = null;

    isMobileView = () => {
        try {
            return window.innerWidth < 768;
        } catch (e) {
            return false;
        }
    }

    openMobileModal = async (type, event) => {
        if (!event) return;

        if (type === 'info') {
            // prefetch info
            this.fetchEventDetailsForInfo(event.eventId);
        }

        this.setState({
            mobileModal: {
                show: true,
                type,
                event
            }
        });
    }

    closeMobileModal = () => {
        this.setState({ mobileModal: { show: false, type: '', event: null } });
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        window.addEventListener('scroll', this.handleWindowScroll, true);
        const locationState = this.props.location && this.props.location.state ? this.props.location.state : {};
        
        // Initialize language from localStorage (user's explicit choice via toggle)
        // This takes priority over URL-based detection
        const storedLanguage = localStorage.getItem('key');
        if (storedLanguage) {
            setLanguage(storedLanguage);
        } else {
            // Fallback: Auto-detect language from URL content only if no explicit choice exists
            try {
                const currentUrl = window.location.href;
                const currentLanguage = getLanguage();
                const hasMarathi = detectMarathiInUrl(currentUrl);
                
                // If URL contains Marathi characters, switch to Marathi
                if (hasMarathi && currentLanguage !== 'ma') {
                    setLanguage('ma');
                    localStorage.setItem('key', 'ma');
                }
                // If URL is in English (no Marathi characters), switch to English
                else if (!hasMarathi && currentLanguage !== 'en') {
                    setLanguage('en');
                    localStorage.setItem('key', 'en');
                }
            } catch (e) {
                console.error('Failed to detect language from URL', e);
            }
        }
        
        // Filters are already set from constructor, just fetch options and events
        this.fetchFilterOptions().then((filterOptionsData) => {
            // Resolve IDs to names if present in URL
            if (filterOptionsData && this.pendingIdResolve) {
                const resolved = this.resolveIdsToNames(filterOptionsData);
                if (resolved) {
                    const updatedFilters = { ...this.state.filters };
                    
                    // Update filters with resolved names from IDs
                    if (resolved.eventType) {
                        updatedFilters.events = [resolved.eventType];
                        this.setState({ categoryName: resolved.eventType });
                    }
                    if (resolved.topics && resolved.topics.length > 0) {
                        updatedFilters.topics = resolved.topics;
                    }
                    if (resolved.subtopicIds && resolved.subtopicIds.length > 0) {
                        updatedFilters.subtopicIds = resolved.subtopicIds;
                    }
                    
                    // Update state with resolved filters
                    this.setState({ filters: updatedFilters }, () => {
                        this.fetchFilteredEvents(updatedFilters);
                    });
                    
                    // Clear pending resolve
                    this.pendingIdResolve = null;
                    return;
                }
            }
            
            // If coming back from calendar → restore calendar state
            if (locationState && locationState.from === 'calendar') {
                this.setState(
                    {
                        viewMode: locationState.viewMode || 'calendar',
                        currentMonth: (locationState.month !== undefined && locationState.month !== null) ? locationState.month : this.state.currentMonth,
                        currentYear: (locationState.year !== undefined && locationState.year !== null) ? locationState.year : this.state.currentYear
                    },
                    () => {
                        this.fetchFilteredEvents(this.state.filters);
                    }
                );
            } else {
                // Normal page load
                this.fetchFilteredEvents(this.state.filters);
            }
        });
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleWindowScroll, true);
        // Clean up location popup timer
        if (this.locationPopupTimer) {
            clearTimeout(this.locationPopupTimer);
            this.locationPopupTimer = null;
        }
    }


    // Fetch filter options from API
    fetchFilterOptions = async () => {
        try {
            const language = getLanguage();
            const langParam = language === 'ma' ? 'ma' : 'en';
            
            // Fetch months from new Node API
            const monthResponse = await fetch(`${myConstClass.NODE_API_BASE}/calendar/months/${langParam}`);
            const monthData = await monthResponse.json();
            
            // Fetch topics and cities from new Node API filterOptions endpoint with language parameter
            const filterOptionsResponse = await fetch(`${myConstClass.NODE_API_BASE}/event/filterOptions?language=${langParam}`);
            const filterOptionsData = await filterOptionsResponse.json();
            
            // Extract months from response
            const months = (monthData.status === 'true' && monthData.data) ? monthData.data : [];
            
            // Extract topics and cities from filterOptions response
            const topics = (filterOptionsData.status === 'true' && filterOptionsData.data) 
                ? (filterOptionsData.data.topics || []) 
                : [];
            const cities = (filterOptionsData.status === 'true' && filterOptionsData.data) 
                ? (filterOptionsData.data.cities || []) 
                : [];
            
            const filterOptions = {
                eventTypes: [],
                topics: topics,
                ageGroups: [],
                months: months,
                cities: cities
            };
            
            this.setState({
                filterOptions: filterOptions
            });
            
            return filterOptions; // Return filter options for ID resolution
        } catch (error) {
            console.error('Error fetching filter options:', error);
            return null;
        }
    }



    // Fetch filtered events from new Node API
    // Uses /api/calendar/list endpoint which returns events grouped by date with full details
    // This API works for both table view and calendar view
    fetchFilteredEvents = async (filters = null) => {
        const currentFilters = filters || this.state.filters;
        
        this.setState({ loading: true });

        try {
            // Content language should ALWAYS come from global language toggle (in header)
            // Not from the language filter - the filter only determines which events to show
            const globalLanguage = getLanguage();
            const langParam = globalLanguage === 'ma' ? 'ma' : 'en'; // Content language from global toggle
            
            // Map language filter to languageId for filtering events (not content language)
            let languageId = null; // null means 'All' - show events in all languages
            
            if (currentFilters.language && currentFilters.language !== 'All') {
                // Map filter language to languageId (this filters which events to show, not content language)
                if (currentFilters.language === 'English' || currentFilters.language === 'english') {
                    languageId = 1;
                } else if (currentFilters.language === 'Marathi' || currentFilters.language === 'marathi') {
                    languageId = 2;
                } else if (currentFilters.language === 'Hindi' || currentFilters.language === 'hindi') {
                    languageId = 3;
                }
            }
            
            // Map topic filter to topic_id (0 for All)
            let topic_id = 0;
            if (currentFilters.topics && currentFilters.topics.length > 0 && !currentFilters.topics.includes('All')) {
                const topicName = currentFilters.topics[0];
                topic_id = this.mapTopicNameToId(topicName);
                if (!topic_id) topic_id = 0;
            }
            
            // Map city filter to city_id (0 for All)
            let city_id = 0;
            if (currentFilters.city && currentFilters.city !== 'All') {
                city_id = currentFilters.city;
            }
            
            // Map event type filter to event_type_id (0 for All)
            // Supports multiple event types (comma-separated)
            let event_type_id = 0;
            
            if (currentFilters.events && currentFilters.events.length > 0 && !currentFilters.events.includes('All')) {
                event_type_id = this.mapEventTypeNamesToIds(currentFilters.events);
                
                if (!event_type_id || event_type_id === null || event_type_id === undefined) {
                    event_type_id = 0;
                }
            }
            
            // Map month filter (format: "YYYY-MM-DD" or "all")
            let start_date = 'all';
            if (currentFilters.month && currentFilters.month !== 'All') {
                start_date = currentFilters.month;
            }
            
            // Build API URL: /api/calendar/list/:language?/:topic_id?/:city_id?/:event_type_id?/:start_date?
            // Always include all parameters to ensure event_type_id is always passed when set
            // This matches the Postman example: /api/calendar/list/en/0/0/2/all
            let apiUrl = `${myConstClass.NODE_API_BASE}/calendar/list/${langParam}`;
            
            // Always include topic_id, city_id, and event_type_id (use 0 if not specified)
            // This ensures event_type_id is always included in the URL when it's set
            apiUrl += `/${topic_id}`;
            apiUrl += `/${city_id}`;
            apiUrl += `/${event_type_id}`;
            
            // Always include start_date (use 'all' to fetch all months)
            apiUrl += `/${start_date}`;
            
            // Add languageId as query parameter if language filter is selected
            if (languageId !== null) {
                apiUrl += `?languageId=${languageId}`;
            }
            
            const response = await fetch(apiUrl);
            const responseData = await response.json();
            
            if (responseData.status === 'true' && responseData.data && Array.isArray(responseData.data)) {

                // Transform API response - pass the selected language filter so we can infer language when API doesn't provide it
                const transformedEvents = this.transformNewApiEventsToCalendarFormat(responseData.data, currentFilters.language);

                // ✅ APPLY FRONTEND FILTERS (duration & status)
                let filteredEvents = transformedEvents;

                /* =========================
                    DURATION FILTER
                    ========================= */
                if (currentFilters.duration && currentFilters.duration !== 'All') {
                        filteredEvents = filteredEvents.filter(event => {
                            // ✅ Ensure duration is always a valid number
                            const d = parseInt(event.duration, 10);

                            if (isNaN(d)) return false;

                            if (currentFilters.duration === '1 day') {
                                return d === 1;
                            }

                            if (currentFilters.duration === '2 days') {
                                return d === 2;
                            }

                            if (currentFilters.duration === '3 days') {
                                return d === 3;
                            }

                            if (currentFilters.duration === '4+ days') {
                                return d >= 4;
                            }

                            return true;
                        });
                    }

                /* =========================
                EVENT STATUS FILTER
                ========================= */
                if (currentFilters.eventStatus === 'Active') {
                    filteredEvents = filteredEvents.filter(event => event.status === 'Open');
                }

                this.setState(
                    {
                        events: filteredEvents,
                        loading: false
                    },
                    () => {
                        // 🔥 PREFETCH TOPIC + COLOR FOR ALL EVENTS
                        filteredEvents.forEach(event => {
                            if (!this.state.eventDetailsCache[event.eventId]?.topicName) {
                                this.fetchEventTopic(event.eventId);
                            }
                        });
                    }
                );


            } else {
                console.error('API Error: Invalid response format', responseData);
                this.setState({
                    events: [],
                    loading: false
                });
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            this.setState({
                events: [],
                loading: false
            });
        }
    }
    
    // Map topic name to ID for new Node API (using filterOptions format)
    mapTopicNameToId = (topicName) => {
        const { filterOptions } = this.state;
        
        if (filterOptions.topics && filterOptions.topics.length > 0) {
            const normalizedInput = topicName ? topicName.toString().trim().toLowerCase() : '';

            const extractVariants = (t) => [
                t.name,
                t.nameEn,
                t.nameMr,
                t.title,
                t.titleEn,
                t.titleMr,
                t.topic_title,
                t.topic_title_en,
                t.topic_title_mr,
                t.label
            ].filter(Boolean).map(v => v.toString().trim().toLowerCase());

            const topic = filterOptions.topics.find(t => {
                const variants = extractVariants(t);
                const topicId = t.id || t.topic_id || t.value;

                // Exact match or numeric id match
                if (variants.some(v => v === normalizedInput)) return true;
                if (topicId && topicId.toString() === normalizedInput) return true;

                // Substring/partial match (handles cases like "Nurturing Kids: Birth up to 7 years" vs "Birth up to 7 years")
                if (variants.some(v => v.includes(normalizedInput) || normalizedInput.includes(v))) return true;

                // Try matching after removing text before ':' or inside parentheses
                const afterColon = normalizedInput.includes(':') ? normalizedInput.split(':').pop().trim() : normalizedInput;
                if (variants.some(v => v.includes(afterColon) || afterColon.includes(v))) return true;
                const withoutParens = normalizedInput.replace(/\(.*\)/, '').trim();
                if (variants.some(v => v.includes(withoutParens) || withoutParens.includes(v))) return true;

                return false;
            });

            if (topic) {
                return topic.id || topic.topic_id || topic.value || 0;
            }
        }
        
        return 0;
    }
    
    // Map event type name(s) to ID(s) for new Node API
    // Supports multiple event types (comma-separated IDs)
    mapEventTypeNamesToIds = (eventTypeNames) => {
        if (!eventTypeNames || eventTypeNames.length === 0 || eventTypeNames.includes('All')) {
            return 0; // Return 0 for "All" or no selection
        }
        
        // Map event type names to API IDs based on API documentation
        // API IDs: 1 = Residential Study Course, 2 = 1 Day Workshop, 3 = Rational Poojas, 4 = Free Event
        const eventTypeMap = {
            'Residential Study Courses': 1,
            'Residential Study Course': 1,
            'One Day Workshops': 2,
            'One Day Workshop': 2,
            '1-Day Workshops': 2,
            '1-Day Workshop': 2,
            'Workshop': 2,
            'Rational Poojas': 4,
            'Rational Pooja': 4,
            'Free Events': 5,
            'Free Event': 5
        };
        
        // Handle array of event type names
        const eventTypeIds = [];
        eventTypeNames.forEach(eventTypeName => {
            const normalizedName = eventTypeName ? eventTypeName.trim() : '';
            
            // Try exact match first
            if (eventTypeMap[normalizedName]) {
                eventTypeIds.push(eventTypeMap[normalizedName]);
                return;
            }
            
            // Try case-insensitive match
            const lowerName = normalizedName.toLowerCase();
            for (const [key, value] of Object.entries(eventTypeMap)) {
                if (key.toLowerCase() === lowerName) {
                    eventTypeIds.push(value);
                    return;
                }
            }
        });
        
        // If no matches found, return 0 (all event types)
        if (eventTypeIds.length === 0) {
            return 0;
        }
        
        // If multiple event types selected, return comma-separated string
        // If single event type, return the ID
        return eventTypeIds.length > 1 ? eventTypeIds.join(',') : eventTypeIds[0];
    }

    // Resolve IDs from URL to names using filterOptions (for deep links with IDs)
    resolveIdsToNames = (filterOptions) => {
        if (!this.pendingIdResolve) return null;

        const resolved = {
            eventType: null,
            topics: [],
            subtopics: []
        };

        // Resolve event type ID to name using API filterOptions
        if (this.pendingIdResolve.eventTypeId) {
            const eventTypeId = parseInt(this.pendingIdResolve.eventTypeId, 10);
            
            if (!isNaN(eventTypeId) && filterOptions && filterOptions.eventTypes && filterOptions.eventTypes.length > 0) {
                const eventType = filterOptions.eventTypes.find(et => {
                    const id = et.id || et.event_type_id || et.eventTypeId;
                    return id === eventTypeId;
                });
                if (eventType) {
                    // Check for nameEn first (matching API response), then name, label, title
                    resolved.eventType = eventType.nameEn || eventType.name || eventType.label || eventType.title || null;
                }
            }
            
            // Fallback to hardcoded mapping if API didn't have event types or event type not found
            if (!resolved.eventType && !isNaN(eventTypeId)) {
                const eventTypeIdToName = {
                    1: 'Residential Study Courses',
                    2: 'One Day Workshops',
                    4: 'Rational Poojas',
                    5: 'Free Events'
                };
                resolved.eventType = eventTypeIdToName[eventTypeId] || null;
            }
        }

        // Resolve topic IDs to names
        if (this.pendingIdResolve.topicIds && this.pendingIdResolve.topicIds.length > 0) {
            if (filterOptions && filterOptions.topics) {
                resolved.topics = this.pendingIdResolve.topicIds
                    .map(topicId => {
                        const topic = filterOptions.topics.find(t => 
                            (t.id || t.topic_id || t.topicId) === topicId
                        );
                        if (topic) {
                            return topic.name || topic.nameEn || topic.label || topic.title || topic.titleEn;
                        }
                        return null;
                    })
                    .filter(name => name !== null);
            }
        }

        // Subtopic IDs would need subtopic data which we don't have at filter options load time
        if (this.pendingIdResolve.subtopicIds && this.pendingIdResolve.subtopicIds.length > 0) {
            resolved.subtopicIds = this.pendingIdResolve.subtopicIds;
        }

        return resolved;
    }


    // Helper function to map languageId to language name
    getLanguageNameFromId = (languageId) => {
        // Convert to number to handle both string and number inputs
        const langId = Number(languageId);
        
        // Map languageId from API to display name
        const languageMap = {
            1: 'English',
            2: 'Marathi',
            3: 'Hindi'
        };
        
        const result = languageMap[langId] || 'English';
        return result;
    }

    // Transform new Node API event format to calendar table format
    // New API returns: [{ date: "Fri, 15th Nov", startDate: "2025-11-15", arrSchedule: [{ scheduleId, eventId, title, ... }] }]
    // languageFilter: the selected language filter (English/Marathi/Hindi/All) to infer language when API doesn't provide it
    transformNewApiEventsToCalendarFormat = (apiData, languageFilter = 'All') => {
    const currentLanguage = getLanguage();
    const isMarathi = currentLanguage === 'ma';

    const eventMap = new Map(); // 🔑 prevents duplicates

    apiData.forEach(dateGroup => {
        const date = dateGroup.date || '';
        const startDate = dateGroup.startDate || '';
        const events = dateGroup.arrSchedule || [];

        events.forEach(event => {
            // Extract start and end dates using same field names as EventDetailsPage
            const eventStartDateStr = event.startDateOriginal || event.startDate || startDate || '';
            const eventEndDateStr = event.endDateOriginal || event.endDate || event.end_date || '';
            const uniqueKey = `${event.eventId || event.scheduleId}-${eventStartDateStr}`;

            // 🚫 Skip duplicate event
            if (eventMap.has(uniqueKey)) return;

            // Determine language label using languageId (primary) or language code (fallback)
            let languageLabel = 'English'; // default

            // Priority 1: languageId from API (numeric field)
            if (event.languageId !== undefined && event.languageId !== null) {
                languageLabel = this.getLanguageNameFromId(event.languageId);
            } else if (event.language_id !== undefined && event.language_id !== null) {
                languageLabel = this.getLanguageNameFromId(event.language_id);
            } else {
                // Fallback: Try language code fields
                const eventLanguage = event.language || event.event_language || event.lang || event.languageCode || '';
                const langCode = eventLanguage.toLowerCase().trim();

                if (langCode === 'ma' || langCode === 'marathi' || langCode === 'mr') {
                    languageLabel = 'Marathi';
                } else if (langCode === 'hi' || langCode === 'hindi') {
                    languageLabel = 'Hindi';
                } else if (langCode === 'en' || langCode === 'english') {
                    languageLabel = 'English';
                } else if (eventLanguage) {
                    // If we have some language value but didn't match above, use it as is
                    languageLabel = eventLanguage;
                } else {
                    // Final fallback: Use the selected language filter
                    // If a specific language is filtered, all returned events should be in that language
                    if (languageFilter && languageFilter !== 'All') {
                        languageLabel = languageFilter;
                    }
                    // If 'All' is selected, we can't infer - keep default 'English'
                }
            }

            const fullAddress = event.venueAddress || '';
            const cityName = event.cityName || this.extractCityName(fullAddress);
            const googleMapsUrl = event.googleMapsUrl || event.url || '';

            const isFull = event.isFull === '1' || event.isFull === 1 || event.isFull === true;
            const status = isFull ? 'Full' : 'Open';

            let dateKey = date;
            let eventDate = null;
            let eventEndDate = null;

            // Parse start date
            if (eventStartDateStr) {
                const parts = eventStartDateStr.split('-');
                if (parts.length === 3) {
                    const [year, month, day] = parts;
                    eventDate = new Date(+year, +month - 1, +day);
                    if (!isNaN(eventDate.getTime())) {
                        dateKey = `${year}-${month}-${day}`;
                    } else {
                        eventDate = null;
                    }
                }
            }

            // Parse end date
            if (eventEndDateStr) {
                const parts = eventEndDateStr.split('-');
                if (parts.length === 3) {
                    const [year, month, day] = parts;
                    eventEndDate = new Date(+year, +month - 1, +day);
                    if (isNaN(eventEndDate.getTime())) {
                        eventEndDate = null;
                    }
                }
            }

            // Calculate duration from dates if both available, otherwise use provided duration
            // Priority: Calculate from dates if both available (most accurate)
            // Fallback: Use provided duration only if valid (> 0)
            let duration = 1;
            if (eventDate && eventEndDate) {
                // Calculate from actual dates (most reliable method)
                const diffMs = eventEndDate.getTime() - eventDate.getTime();
                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                duration = Math.max(1, diffDays + 1); // inclusive
            } else if (event.duration && !isNaN(parseInt(event.duration)) && parseInt(event.duration) > 0) {
                // Use provided duration only if it's a valid positive number
                duration = parseInt(event.duration);
            }

            // Ensure dateKey uses zero-padded YYYY-MM-DD so CalendarView matching works
            const paddedDateKey = eventDate
                ? `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`
                : (dateKey || date);
            
            const categoryColor = getEventCategoryColor(
                    `${event.title || ''} ${event.eventType || ''}`
            );

            eventMap.set(uniqueKey, {
                eventId: event.eventId || event.scheduleId,
                scheduleId: event.scheduleId,
                date,
                dateKey: paddedDateKey,
                startDate: eventDate,
                endDate: eventEndDate,
                // preserve original strings from API so other components can use them
                startDateOriginal: eventStartDateStr,
                endDateOriginal: eventEndDateStr,
                duration,
                name: event.title || '',
                language: languageLabel,
                cityName,
                location: cityName,
                fullAddress,
                googleMapsUrl,
                status,
                isFull,
                categoryColor   
            });
        });
    });

    // ✅ Return deduplicated list
    return Array.from(eventMap.values());
};


    // Extract city name from full address
    extractCityName = (address) => {
        if (!address) return '';
        
        // Common city patterns - try to extract city name
        // Address format is usually: "Address, City" or "City" or "Address, City, State"
        const cityPatterns = [
            /,\s*([^,]+?)(?:,\s*[^,]+)?$/, // Match last part before final comma
            /^([^,]+?)(?:\s*,|$)/, // Match first part if no comma
        ];
        
        for (const pattern of cityPatterns) {
            const match = address.match(pattern);
            if (match && match[1]) {
                return match[1].trim();
            }
        }
        
        // If no pattern matches, return the address as is (fallback)
        return address;
    }

    // Format date string to use short month names (Jan, Feb, etc.)
    formatDateWithShortMonth = (dateString) => {
        if (!dateString) return dateString;
        
        const currentLanguage = getLanguage();
        const isMarathi = currentLanguage === 'ma';
        
        // Full month names mapping to short forms
        const monthMap = isMarathi
            ? {
                'जानेवारी': 'जाने',
                'फेब्रुवारी': 'फेब्रु',
                'मार्च': 'मार्च',
                'एप्रिल': 'एप्रि',
                'मे': 'मे',
                'जून': 'जून',
                'जुलै': 'जुलै',
                'ऑगस्ट': 'ऑग',
                'सप्टेंबर': 'सप्टें',
                'ऑक्टोबर': 'ऑक्टो',
                'नोव्हेंबर': 'नोव्हें',
                'डिसेंबर': 'डिसें'
            }
            : {
                'January': 'Jan',
                'February': 'Feb',
                'March': 'Mar',
                'April': 'Apr',
                'May': 'May',
                'June': 'Jun',
                'July': 'Jul',
                'August': 'Aug',
                'September': 'Sep',
                'October': 'Oct',
                'November': 'Nov',
                'December': 'Dec'
            };
        
        // Replace full month names with short forms
        let formattedDate = dateString;
        Object.keys(monthMap).forEach(fullMonth => {
            const regex = new RegExp(fullMonth, 'gi');
            formattedDate = formattedDate.replace(regex, monthMap[fullMonth]);
        });
        
        return formattedDate;
    }

    formatDateRange = (startDate, endDate, fallbackDate) => {
        // If startDate is missing, fallback to existing date string
        if (!startDate) return this.formatDateWithShortMonth(fallbackDate);

        const format = (dateObj) =>
            dateObj.toLocaleDateString('en-GB', {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
            });

        // Single-day event
        if (!endDate || startDate.toDateString() === endDate.toDateString()) {
            return format(startDate);
        }

        // Multi-day event
        return `${format(startDate)} – ${format(endDate)}`;
    };


    // Group events by date for table display
    groupEventsByDate = (events) => {
        const grouped = {};

        events.forEach(event => {
            const start = event.startDate
                ? event.startDate.toISOString().split('T')[0]
                : event.dateKey;

            const end = event.endDate
                ? event.endDate.toISOString().split('T')[0]
                : start;

            // 🔑 KEY = start + end
            const groupKey = `${start}_${end}`;

            if (!grouped[groupKey]) {
                grouped[groupKey] = {
                    date: event.date,
                    events: []
                };
            }

            grouped[groupKey].events.push(event);
        });

        return Object.values(grouped);
    };

    handleFilterChange = (filters) => {
        // Merge with existing filters (including month)
        const updatedFilters = {
            ...this.state.filters,
            ...filters
        };
        // If month filter changed, update calendar view month/year as well
        if (updatedFilters.month && updatedFilters.month !== 'All') {
            // expected format: YYYY-MM-DD (we only need year and month)
            const parts = String(updatedFilters.month).split('-');
            if (parts.length >= 2) {
                const year = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1; // 0-based
                if (!isNaN(year) && !isNaN(month)) {
                    this.setState({ currentMonth: month, currentYear: year });
                }
            }
        }

        // Update URL search params so the current filter state is shareable and navigable
        try {
            const params = new URLSearchParams();
            // Note: Do not set `eventType` in query params when the event type
            // is being encoded into the pathname (to avoid duplicate info).
            // The `eventType` query param will be added only if no specific
            // event type is placed into the pathname below.
            // topics
            if (updatedFilters.topics && updatedFilters.topics.length > 0 && !updatedFilters.topics.includes('All')) {
                params.set('topics', updatedFilters.topics.join(','));
            }
            // language
            if (updatedFilters.language && updatedFilters.language !== 'All') {
                params.set('language', updatedFilters.language);
            }
            // subTopics
            if (updatedFilters.subTopics && updatedFilters.subTopics.length > 0 && !updatedFilters.subTopics.includes('All')) {
                params.set('subTopics', updatedFilters.subTopics.join(','));
            }
            // month
            if (updatedFilters.month && updatedFilters.month !== 'All') {
                params.set('month', updatedFilters.month);
            }
            // city
            if (updatedFilters.city && updatedFilters.city !== 'All' && updatedFilters.city !== 'Select') {
                params.set('city', updatedFilters.city);
            }

            // Include event type name and ID in the pathname (if a specific type is selected)
            const selectedEvent = updatedFilters.events && updatedFilters.events.length > 0 && updatedFilters.events[0] !== 'All'
                ? updatedFilters.events[0]
                : null;

            // If no event type will be included in the pathname, include it in query params
            if (!selectedEvent && updatedFilters.events && updatedFilters.events.length > 0 && updatedFilters.events[0] !== 'All') {
                params.set('eventType', updatedFilters.events[0]);
            }

            if (selectedEvent) {
                const eventTypeId = this.mapEventTypeNamesToIds([selectedEvent]);
                // Use hardcoded base path (like EventListingPage does with /booknow)
                const basePath = '/event-calendar';

                const pathname = eventTypeId
                    ? `${basePath}/${encodeURIComponent(selectedEvent)}/${eventTypeId}`
                    : basePath;

                // Update state with new category info (like EventListingPage)
                this.setState({
                    categoryName: selectedEvent,
                    categoryId: eventTypeId ? eventTypeId.toString() : ''
                });

                this.props.history.push({ pathname, search: params.toString(), state: updatedFilters });
            } else {
                // No specific event type selected - push to base calendar path
                const basePath = '/event-calendar';
                
                this.setState({
                    categoryName: '',
                    categoryId: ''
                });
                
                this.props.history.push({ pathname: basePath, search: params.toString(), state: updatedFilters });
            }
        } catch (err) {
            console.error('Error updating URL with filters', err);
        }

        this.setState({ filters: updatedFilters });
        this.fetchFilteredEvents(updatedFilters);
    }

    handleMonthFilterChange = (monthValue) => {
        const updatedFilters = {
            ...this.state.filters,
            month: monthValue
        };
        
        // Update current month/year if a specific month is selected
        if (monthValue && monthValue !== 'All') {
            const [year, month] = monthValue.split('-');
            this.setState({
                currentMonth: parseInt(month) - 1,
                currentYear: parseInt(year)
            });
        }
        
        this.setState({ filters: updatedFilters });
        this.fetchFilteredEvents(updatedFilters);
    }

    handleViewModeChange = (mode) => {
        const { history, location } = this.props;

        this.setState({ viewSwitching: true });

        setTimeout(() => {
            this.setState({ 
                viewMode: mode,
                viewSwitching: false 
            });

            // 🔥 PUSH VIEW MODE INTO BROWSER HISTORY
            history.replace({
                pathname: location.pathname,
                state: {
                    ...(location.state || {}),
                    viewMode: mode,
                    from: 'calendar',
                    month: this.state.currentMonth,
                    year: this.state.currentYear
                }
            });
        }, 300);
    };


    // Handle location icon click - toggle popup with address
    handleLocationClick = (e, address, googleMapsUrl) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Check if popup is already open for this location - if so, close it
        const { locationPopup } = this.state;
        if (locationPopup.show && locationPopup.address === address) {
            this.closeLocationPopup();
            return;
        }
        
        const rect = e.currentTarget.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Calculate position - position directly below the icon with arrow pointing to icon center
        // getBoundingClientRect() gives position relative to viewport (perfect for position: fixed)
        const popupWidth = 220;
        const popupHeight = 120;
        const iconCenterX = rect.left + (rect.width / 2);
        
        // Position popup below the icon (5px gap)
        let top = rect.bottom + 5;
        
        // Position popup so arrow (at center top) aligns with icon center
        // Arrow is at the top center of popup, so popup left should be icon center minus half popup width
        let left = iconCenterX - (popupWidth / 2);
        let arrowPosition = popupWidth / 2; // Arrow at center top of popup
        
        // Adjust if popup would go off right edge
        if (left + popupWidth > viewportWidth) {
            left = viewportWidth - popupWidth - 10;
            // Recalculate arrow position relative to icon center
            arrowPosition = iconCenterX - left;
            // Clamp arrow position within popup bounds
            if (arrowPosition < 10) arrowPosition = 10;
            if (arrowPosition > popupWidth - 10) arrowPosition = popupWidth - 10;
        }
        
        // Adjust if popup would go off left edge
        if (left < 10) {
            left = 10;
            // Recalculate arrow position relative to icon center
            arrowPosition = iconCenterX - left;
            // Clamp arrow position within popup bounds
            if (arrowPosition < 10) arrowPosition = 10;
            if (arrowPosition > popupWidth - 10) arrowPosition = popupWidth - 10;
        }
        
        // Adjust if popup would go off bottom edge - show above instead
        if (top + popupHeight > viewportHeight) {
            top = rect.top - popupHeight - 5; // Show above with 5px gap
        }
        
        // Use viewport coordinates (no scroll offsets needed for position: fixed)
        this.setState({
            locationPopup: {
                show: true,
                address: address || '',
                googleMapsUrl: googleMapsUrl || '',
                position: {
                    top: top,
                    left: left,
                    arrowLeft: arrowPosition // Horizontal position of arrow from left edge of popup
                }
            }
        });
    }

    // Share / Deep-link handler for table/mobile events
    handleShareEvent = async (event, e) => {
        if (e && e.stopPropagation) e.stopPropagation();

        if (!event || (!event.eventId && !event.scheduleId)) {
            toast.error('Unable to share: Event data missing');
            return;
        }

        try {
            const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
            const eventId = event.eventId || event.event_id || event.id;
            const scheduleId = event.scheduleId || event.schedule_id;
            let url = `${origin}/event_details?event_id=${eventId}`;
            if (scheduleId) {
                url += `&schedule_id=${scheduleId}`;
            }
            // Category: Prefer event.categoryLabel, fallback to event.topicName, fallback to 'Event'
            const category = event.categoryLabel || event.topicName || 'Event';
            // Date range: Prefer event.dateRange, fallback to event.startDateOriginal, fallback to event.startDate or event.date
            const dateRange = event.dateRange || event.startDateOriginal || event.startDate || event.date || '';
            // Location
            const location = event.location || event.venueAddress || '';
            // Description: up to 100 chars, plain text
            let shortDesc = '';
            if (event.description) {
                const stripped = String(event.description).replace(/<[^>]*>/g, '');
                const normalized = stripped.replace(/\s+/g, ' ').trim();
                if (normalized.length <= 100) {
                    shortDesc = normalized;
                } else {
                    const lastSpace = normalized.lastIndexOf(' ', 100);
                    if (lastSpace > 0) {
                        shortDesc = normalized.slice(0, lastSpace).trim() + '...';
                    } else {
                        shortDesc = normalized.slice(0, 100).trim() + '...';
                    }
                }
            }

            // Compose the share text (do NOT include the URL here for native share)
            const shareText = `Book the ${category}: ${event.name || ''}${dateRange ? ` – ${dateRange}` : ''}${location ? ` at ${location}` : ''}\n${shortDesc}`;

            if (typeof navigator !== 'undefined' && navigator.share) {
                await navigator.share({
                    title: event.name || 'Event',
                    text: shareText,
                    url: url
                });
                toast.success('Link copied/shared');
                return true;
            }
            if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(`${shareText}\n${url}`);
                toast.success('Link copied to clipboard!');
                return true;
            }
            window.prompt('Copy this link:', `${shareText}\n${url}`);
            return true;
        } catch (err) {
            console.error('Share failed', err);
            toast.error('Failed to share link');
            return false;
        }
    }

    // ✅ LOCATION HOVER (OPEN POPUP)
    handleLocationHover = (e, address, googleMapsUrl) => {
        e.preventDefault();
        e.stopPropagation();
        // Clear any pending close timer when entering the icon
        if (this.locationPopupTimer) {
            clearTimeout(this.locationPopupTimer);
            this.locationPopupTimer = null;
        }
        this.handleLocationClick(e, address, googleMapsUrl);
    };

    // ✅ LOCATION LEAVE (START CLOSE TIMER)
    handleLocationLeave = () => {
        // Delay closing to allow user to move to popup
        if (!this.locationPopupTimer) {
            this.locationPopupTimer = setTimeout(() => {
                this.closeLocationPopup();
                this.locationPopupTimer = null;
            }, 150); // 150ms delay to allow smooth mouse movement to popup
        }
    };

    // ✅ LOCATION POPUP ENTER (CANCEL CLOSE TIMER)
    handleLocationPopupEnter = () => {
        // Cancel the close timer when entering the popup
        if (this.locationPopupTimer) {
            clearTimeout(this.locationPopupTimer);
            this.locationPopupTimer = null;
        }
    };

    // ✅ LOCATION POPUP LEAVE (START CLOSE TIMER)
    handleLocationPopupLeave = () => {
        // Delay closing to allow smooth mouse movement
        if (!this.locationPopupTimer) {
            this.locationPopupTimer = setTimeout(() => {
                this.closeLocationPopup();
                this.locationPopupTimer = null;
            }, 150); // 150ms delay
        }
    };

    // Close location popup
    closeLocationPopup = () => {
        // Clear any pending timer
        if (this.locationPopupTimer) {
            clearTimeout(this.locationPopupTimer);
            this.locationPopupTimer = null;
        }
        this.setState({
            locationPopup: {
                show: false,
                address: '',
                googleMapsUrl: '',
                position: { top: 0, left: 0, arrowLeft: 15 }
            },
             eligibilityPopup: {
                show: false,
                event: null,
                position: { top: 0, left: 0 }
            },
            infoPopup: {
                show: false,
                event: null,
                position: { top: 0, left: 0 }
            }
        });
    }

    // ✅event info
    handleEligibilityInfoClick = (e, event) => {
        e.preventDefault();
        e.stopPropagation();

        const rect = e.currentTarget.getBoundingClientRect();
        const POPUP_WIDTH = 220;
        const GAP = 8;
        const viewportWidth = window.innerWidth;

        let left = rect.left; 
        if (rect.left + POPUP_WIDTH > viewportWidth) { 
            left = rect.right - POPUP_WIDTH; 
        } 
        left = left - 100;
        const top = rect.bottom + GAP;

        this.setState(prev => {
            const isSameEventEligibilityOpen =
                prev.eligibilityPopup.show &&
                prev.eligibilityPopup.event?.eventId === event.eventId;

            return {
                eligibilityPopup: isSameEventEligibilityOpen
                    ? { show: false, event: null, position: { top: 0, left: 0 } }
                    : { show: true, event, position: { top, left } },

                // 🔒 ALWAYS close info popup when opening eligibility
                infoPopup: { show: false, event: null, position: { top: 0, left: 0 } }
            };
        });

    };
    handleEligibilityHover = (e, event) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleEligibilityInfoClick(e, event);
    };

    handleEligibilityLeave = () => {
        this.setState({
            eligibilityPopup: { show: false, event: null, position: { top: 0, left: 0 } }
        });
    };


    fetchEventDetailsForInfo = async (eventId) => {
        // if (!eventId || this.state.eventDetailsCache[eventId]) return;
        if (!eventId || this.state.eventDetailsCache[eventId]?.description || this.state.eventDetailsCache[eventId]?.shortDescription || this.state.eventDetailsCache[eventId]?.eventDescription) return;

        this.setState({ infoLoading: true });

        try {
            const language = getLanguage();
            const userId = localStorage.getItem('user_id') || 0;

            const apiUrl = `${myConstClass.BASE_URL}event/getEventInfoFrontEnd/${eventId}/${language}/0/${userId}`;

            const response = await fetch(apiUrl);
            if (!response.ok) throw new Error('Failed to fetch');

            const data = await response.json();
            const eventData = data.data || data;           

            this.setState(prev => ({
                eventDetailsCache: {
                    ...prev.eventDetailsCache,
                    [eventId]: eventData
                }
            }));
        } catch (err) {
            console.error('Info popup fetch failed:', err);
        } finally {
            this.setState({ infoLoading: false });
        }
    };

    //Add function to fetch TOPIC NAME ONLY
    fetchEventTopic = async (eventId) => {
        if (!eventId) return;
        if (this.state.eventDetailsCache[eventId]?.topicName) return;

        try {
            const currentLanguage = getLanguage();
            const langParam = currentLanguage === 'ma' ? 'ma' : 'en';
            const response = await fetch(
                `${myConstClass.BASE_URL}event/getEventInfo/${eventId}?language=${langParam}`
            );
            const data = await response.json();

            if (data.currentTopicIds?.length > 0) {
                const topicName = data.currentTopicIds[0].label;
                const categoryColor = getEventCategoryColor(topicName);

                this.setState(prev => ({
                    eventDetailsCache: {
                        ...prev.eventDetailsCache,
                        [eventId]: {
                            ...(prev.eventDetailsCache[eventId] || {}),
                            topicName,
                            categoryColor
                        }
                    }
                }));
            } else {
            }
        } catch (err) {
            console.error('Topic fetch failed', err);
        }
    };


   handleTopicHover = async (e, event) => {
        e.stopPropagation();

        const rect = e.currentTarget.getBoundingClientRect();

        await this.fetchEventTopic(event.eventId);

        const cached = this.state.eventDetailsCache[event.eventId];

        this.setState({
            topicTooltip: {
                show: true,
                eventId: event.eventId,
                text: cached?.topicName || 'Topic not available',
                position: {
                    top: rect.top - 30,
                    left: rect.left + rect.width / 2
                }
            }
        });
    };


    handleTopicLeave = () => {
        this.setState({
            topicTooltip: {
                show: false,
                eventId: null,
                text: '',
                position: { top: 0, left: 0 }
            }
        });
    };


    handleInfoIconClick = (e, event) => {
        e.preventDefault();
        e.stopPropagation();

        this.fetchEventDetailsForInfo(event.eventId);

        const rect = e.currentTarget.getBoundingClientRect();
        const POPUP_WIDTH = 220;
        const GAP = 8;
        const viewportWidth = window.innerWidth;

        let left = rect.left; 
        if (rect.left + POPUP_WIDTH > viewportWidth) { 
            left = rect.right - POPUP_WIDTH ; 
        } 
        left = left - 100;
        //console.log('left',left);
        const top = rect.bottom + GAP;

        this.setState(prev => {
            const isSameEventInfoOpen =
                prev.infoPopup.show &&
                prev.infoPopup.event?.eventId === event.eventId;

            return {
                infoPopup: isSameEventInfoOpen
                    ? { show: false, event: null, position: { top: 0, left: 0 } }
                    : { show: true, event, position: { top, left } },

                // 🔒 ALWAYS close eligibility popup when opening info
                eligibilityPopup: { show: false, event: null, position: { top: 0, left: 0 } }
            };
        });
    };

    handleInfoHover = (e, event) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleInfoIconClick(e, event);
    };

    handleInfoLeave = () => {
        this.setState({
            infoPopup: { show: false, event: null, position: { top: 0, left: 0 } }
        });
    };


    getFirstLineOfDescription = (event) => {
        if (!event) return '';

        const raw =
            event.shortDescription ||
            event.description ||
            event.eventDescription ||
            '';

        // Remove HTML tags
        const text = raw.replace(/<[^>]*>/g, '').trim();

        // Split into lines or sentences
        const parts = text.split(/\r?\n|\./).map(p => p.trim()).filter(Boolean);

        // Return first 2–3 lines/sentences
        return parts.slice(0, 3).join('. ') + (parts.length > 3 ? '…' : '');
    };

    
    handleCalendarMonthChange = (month, year) => {
        this.setState({ 
            currentMonth: month,
            currentYear: year
        });
        
        // Update month filter and refetch events
        const updatedFilters = {
            ...this.state.filters,
            month: `${year}-${String(month + 1).padStart(2, '0')}-01`
        };
        
        this.setState({ filters: updatedFilters });
        this.fetchFilteredEvents(updatedFilters);
    }

        handleWindowScroll = () => {
        this.setState({
            locationPopup: {
                show: false,
                address: '',
                googleMapsUrl: '',
                position: { top: 0, left: 0, arrowLeft: 15 }
            },
            eligibilityPopup: {
                show: false,
                event: null,
                position: { top: 0, left: 0 }
            },
            infoPopup: {
                show: false,
                event: null,
                position: { top: 0, left: 0 }
            },
            topicTooltip: {
                show: false,
                eventId: null,
                text: '',
                position: { top: 0, left: 0 }
            }
        });
    };


    render() {
        const { t } = this.props;
        const { events, loading, viewSwitching, filters, filterOptions, viewMode, currentMonth, currentYear, locationPopup } = this.state;

        return (
            <div className="events-calendar-page" onClick={this.closeLocationPopup}>
                <NewHeader />

                {/* Hero Section - Reusable component from EventListingPage */}
                <EventListingHero 
                    categoryName={t('calendar_hero_title')}
                    categoryId=""
                    description={t('calendar_hero_desc')}
                />

                <div className="container-fluid events-calendar-container">
                    <div className="container-xl">
                        {/* Mobile and Tablet: Filters Above Tabs (Full Width) */}
                        <div className="row d-lg-none mb-3">
                            <div className="col-12">
                                <EventsCalendarFilters 
                                    onFilterChange={this.handleFilterChange}
                                    initialFilters={filters}
                                    filterOptions={filterOptions}
                                    viewMode={viewMode}
                                />
                            </div>
                        </div>
                        
                        <div className="row">
                            {/* Left Column - Filters (Desktop Only - Large screens) */}
                            <div className="col-12 col-lg-3 mb-4 d-none d-lg-block">
                                <EventsCalendarFilters 
                                    onFilterChange={this.handleFilterChange}
                                    initialFilters={filters}
                                    filterOptions={filterOptions}
                                    viewMode={viewMode}
                                />
                            </div>
                            
                            {/* Right Column - View Toggle and Content */}
                            <div className="col-12 col-lg-9">
                                {/* View Toggle Buttons */}
                                <div className="view-toggle-section">
                                    <button
                                        className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                                        onClick={() => this.handleViewModeChange('table')}
                                    >
                                        {t('home.TABLE VIEW')}
                                    </button>
                                    <button
                                        className={`view-toggle-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                                        onClick={() => this.handleViewModeChange('calendar')}
                                    >
                                        {t('home.CALENDAR VIEW')}
                                    </button>
                                </div>

                                {/* Loading State */}
                                {loading && (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading events...</p>
                                    </div>
                                )}

                                {/* View Switching Loader */}
                                {viewSwitching && (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Switching view...</span>
                                        </div>
                                        <p className="mt-2">Switching view...</p>
                                    </div>
                                )}

                                {/* Conditional Rendering: Table View or Calendar View */}
                                {!loading && !viewSwitching && (
                                    <>
                                        {viewMode === 'table' ? (
                                            /* Table View */
                                            events.length > 0 ? (
                                                <>
                                                    {/* Desktop and Tablet Design - Table View */}
                                                    <div className="d-none d-md-block">
                                                        <div className="row">
                                                            <div className="col">
                                                                <table className="table table-sm table-bordered tableResponsive">
                                                                    <thead className="bg-light">
                                                                        <tr className="bg-dark-grey">
                                                                            <th scope="col">{t('home.Date')}</th>
                                                                            <th scope="col">{t('home.Duration')}</th>
                                                                            <th scope="col">{t('home.Name of Event')}</th>
                                                                            <th scope="col">{t('home.Language')}</th>
                                                                            <th scope="col">{t('home.Venue')}</th>
                                                                            <th scope="col">{t('home.Status')}</th>
                                                                            <th scope="col">{t('home.Link')}</th>
                                                                            <th scope="col">{t('home.Share')}</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                    {this.groupEventsByDate(events).map((dateGroup, groupIndex) => {
                                                                        const allSameDateRange = dateGroup.events.every(e =>
                                                                            e.startDate?.getTime() === dateGroup.events[0].startDate?.getTime() &&
                                                                            e.endDate?.getTime() === dateGroup.events[0].endDate?.getTime()
                                                                        );

                                                                        return dateGroup.events.map((event, eventIndex) => (
                                                                            <tr key={`${event.eventId || groupIndex}-${eventIndex}`}>
                                                                                
                                                                                {/* ✅ ONLY DATE LOGIC CHANGED */}
                                                                                {allSameDateRange ? (
                                                                                    eventIndex === 0 && (
                                                                                        <td style={{ verticalAlign: 'middle' }} rowSpan={dateGroup.events.length}>
                                                                                            {this.formatDateRange(
                                                                                                event.startDate,
                                                                                                event.endDate,
                                                                                                dateGroup.date
                                                                                            )}
                                                                                        </td>
                                                                                    )
                                                                                ) : (
                                                                                    <td style={{ verticalAlign: 'middle' }}>
                                                                                        {this.formatDateRange(
                                                                                            event.startDate,
                                                                                            event.endDate,
                                                                                            dateGroup.date
                                                                                        )}
                                                                                    </td>
                                                                                )}

                                                                                {/* ⬇️ EVERYTHING BELOW IS YOUR ORIGINAL CODE ⬇️ */}

                                                                                <td style={{ verticalAlign: 'middle' }}>
                                                                                    {event.duration} {event.duration === 1 ? t('home.day') : t('home.Days')}
                                                                                </td>

                                                                                <td style={{ verticalAlign: 'middle' }}>
                                                                                    <div className="event-name-cell">
                                                                                        <div className="event-name-left">
                                                                                            <span
                                                                                                className={`calendar-event-square calendar-event-square--${
                                                                                                    this.state.eventDetailsCache[event.eventId]?.categoryColor || 'blue'
                                                                                                }`}
                                                                                                onMouseEnter={(e) => this.handleTopicHover(e, event)}
                                                                                                onMouseLeave={this.handleTopicLeave}
                                                                                            />
                                                                                            <span className="event-name-text">{event.name}</span>
                                                                                        </div>

                                                                                        <div className="event-name-icons">
                                                                                            <span
                                                                                                className="event-info-icon"
                                                                                                onMouseEnter={(e) => this.handleEligibilityHover(e, event)}
                                                                                                //onMouseLeave={this.handleEligibilityLeave}
                                                                                            >
                                                                                                E
                                                                                            </span>
                                                                                            <span
                                                                                                className="event-info-icon--i"
                                                                                                onMouseEnter={(e) => this.handleInfoHover(e, event)}
                                                                                                onMouseLeave={this.handleInfoLeave}
                                                                                            >
                                                                                                i
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                                                                    {event.language || 'English'}
                                                                                </td>

                                                                                <td style={{ verticalAlign: 'middle' }}>
                                                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                                        <span>{event.cityName || event.location}</span>
                                                                                        <i 
                                                                                            className="fas fa-map-marker-alt" 
                                                                                            style={{ color: '#dc3545', cursor: 'pointer', marginLeft: '8px' }}
                                                                                            onMouseEnter={(e) =>
                                                                                                this.handleLocationHover(e, event.fullAddress, event.googleMapsUrl)
                                                                                            }
                                                                                            onMouseLeave={this.handleLocationLeave}
                                                                                        />
                                                                                    </div>
                                                                                </td>

                                                                                <td style={{ verticalAlign: 'middle' }}>
                                                                                    {event.status}
                                                                                </td>

                                                                                <td>
                                                                                    <Link
                                                                                        className="btn btn-brown btn-sm calender-list-button"
                                                                                        to={{
                                                                                            pathname: `/event_details/${event.eventId}`,
                                                                                            state: {
                                                                                                selectedScheduleId: event.scheduleId,
                                                                                                from: 'calendar',
                                                                                                viewMode: this.state.viewMode,
                                                                                                month: this.state.currentMonth,
                                                                                                year: this.state.currentYear
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        {getLanguage() === 'ma' ? 'अधिक माहिती' : 'View Details'}
                                                                                    </Link>
                                                                                </td>

                                                                                    <td className="table-share-cell" style={{ verticalAlign: 'middle' }}>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-outline-secondary btn-sm"
                                                                                            onClick={(e) => this.handleShareEvent(event, e)}
                                                                                        >
                                                                                            <i className="fas fa-share-alt"></i>
                                                                                        </button>
                                                                                    </td>
                                                                            </tr>
                                                                        ));
                                                                    })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Mobile Design - Card View */}
                                                    <div className="d-block d-md-none">
                                                        {this.groupEventsByDate(events).map((dateGroup, groupIndex) => (
                                                            dateGroup.events.map((event, eventIndex) => (
                                                                <Card key={`${event.eventId || groupIndex}-${eventIndex}`} className="bg-light my-3">
                                                                    <Row className="px-3">
                                                                        <Col sm="12" xs="12" className="border-bottom py-2">
                                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                                <h6 className="mx-0 text-left mb-0">
                                                                                    {this.formatDateRange(
                                                                                    event.startDate,
                                                                                    event.endDate,
                                                                                    dateGroup.date
                                                                                )}
                                                                                </h6>
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary btn-sm"
                                                                                onClick={(e) => this.handleShareEvent(event, e)}
                                                                                aria-label="Share event"
                                                                            >
                                                                                <i className="fas fa-share-alt"></i>
                                                                            </button>
                                                                            <span
                                                                                className="ml-2 event-info-icon--i"
                                                                                onMouseEnter={(e) => this.handleInfoHover(e, event)}
                                                                                onMouseLeave={this.handleInfoLeave}
                                                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.openMobileModal('info', event); }}
                                                                            >i</span>
                                                                            <span
                                                                                className="event-info-icon"
                                                                                onMouseEnter={(e) => this.handleEligibilityHover(e, event)}
                                                                                onMouseLeave={this.handleEligibilityLeave}
                                                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.openMobileModal('eligibility', event); }}
                                                                            >E</span>
                                                                        </Col>
                                                                        <Col sm="12" xs="12">
                                                                            <Row className="py-2">
                                                                                <Col sm="12" xs="12">
                                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                        <span
                                                                                            className={`calendar-event-square calendar-event-square--${
                                                                                                this.state.eventDetailsCache[event.eventId]?.categoryColor || 'blue'
                                                                                            }`}
                                                                                            onMouseEnter={(e) => this.handleTopicHover(e, event)}
                                                                                            onMouseLeave={this.handleTopicLeave}
                                                                                        />
                                                                                       <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                                        <Link
                                                                                                to={{
                                                                                                    pathname: `/event_details/${event.eventId}`,
                                                                                                    state: {
                                                                                                    selectedScheduleId: event.scheduleId,
                                                                                                        from: 'calendar',
                                                                                                        viewMode: this.state.viewMode,
                                                                                                    month: this.state.currentMonth,
                                                                                                    year: this.state.currentYear
                                                                                                }
                                                                                            }}
                                                                                            >
                                                                                                {event.name}
                                                                                            </Link>
                                                                                    </div>
                                                                                    </div>
                                                                                </Col>
                                                                                <Col sm="12" xs="12">
                                                                                    <small>{t('home.Duration')}&nbsp;:&nbsp;{event.duration}&nbsp;{event.duration === 1 ? t('home.day') : t('home.Days')}</small>
                                                                                </Col>
                                                                                <Col sm="12" xs="12">
                                                                                    <small>{t('home.Language')}&nbsp;:&nbsp;{event.language || 'English'}</small>
                                                                                </Col>
                                                                                <Col sm="12" xs="12">
                                                                                    <small>{t('home.Status')}&nbsp;:&nbsp;{event.status}</small>
                                                                                </Col>
                                                                                <Col sm="12" xs="12">
                                                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                                        <span>{event.cityName || event.location}</span>
                                                                                        <i 
                                                                                            className="fas fa-map-marker-alt" 
                                                                                            style={{ color: '#dc3545', cursor: 'pointer', marginLeft: '8px' }}
                                                                                            onMouseEnter={(e) =>
                                                                                                this.handleLocationHover(e, event.fullAddress, event.googleMapsUrl)
                                                                                            }
                                                                                            onMouseLeave={this.handleLocationLeave}
                                                                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.openMobileModal('location', event); }}
                                                                                        ></i>
                                                                                    </div>
                                                                                </Col>
                                                                            </Row>
                                                                        </Col>
                                                                    </Row>
                                                                </Card>
                                                            ))
                                                        ))}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <p className="text-muted">No events found matching your filters.</p>
                                                </div>
                                            )
                                        ) : (
                                            /* Calendar View */
                                            <CalendarView
                                                events={events}
                                                currentMonth={currentMonth}
                                                currentYear={currentYear}
                                                onMonthChange={this.handleCalendarMonthChange}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {this.state.topicTooltip.show && (
                    <div
                        className="topic-tooltip"
                        style={{
                            position: 'fixed',
                            top: this.state.topicTooltip.position.top,
                            left: this.state.topicTooltip.position.left,
                            transform: 'translateX(-50%)',
                            background: '#333',
                            color: '#fff',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            zIndex: 9999,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {this.state.topicTooltip.text}
                    </div>
                )}

                {this.state.mobileModal.show && this.state.mobileModal.event && (
                    <div
                        className="mobile-modal-overlay"
                        style={{
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0,0,0,0.5)',
                            zIndex: 10000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onClick={(e) => { e.stopPropagation(); this.closeMobileModal(); }}
                    >
                        <div
                            className="mobile-modal-box"
                            style={{
                                background: '#fff',
                                padding: '16px',
                                borderRadius: '8px',
                                maxWidth: '92%',
                                width: 340,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <strong>
                                    {this.state.mobileModal.type === 'location' && 'Address'}
                                    {this.state.mobileModal.type === 'info' && 'Event Information'}
                                    {this.state.mobileModal.type === 'eligibility' && 'Eligibility'}
                                </strong>
                                <button className="btn btn-sm btn-light mob-close-btn" onClick={() => this.closeMobileModal()}>Close</button>
                            </div>
                            <div style={{ marginTop: 8, fontSize: '0.9rem' }}>
                                {this.state.mobileModal.type === 'location' && (
                                    <>
                                        <div style={{ marginBottom: 8, wordBreak: 'break-word' }}>{this.state.mobileModal.event.fullAddress || 'Address not available'}</div>
                                        {this.state.mobileModal.event.googleMapsUrl ? (
                                            <a href={this.state.mobileModal.event.googleMapsUrl} target="_blank" rel="noopener noreferrer">Open in Google Maps</a>
                                        ) : (
                                            <div style={{ color: '#6c757d', fontStyle: 'italic' }}>Google Maps link not available</div>
                                        )}
                                    </>
                                )}

                                {this.state.mobileModal.type === 'eligibility' && (
                                    <div style={{ fontSize: '0.9rem' }}>
                                        <Link to={`/event_details/${this.state.mobileModal.event.eventId}`} onClick={() => this.closeMobileModal()} style={{ color: '#007bff', textDecoration: 'underline' }}>[Check eligibility]</Link>
                                        &nbsp;on the event detail page.
                                    </div>
                                )}

                                {this.state.mobileModal.type === 'info' && (
                                    <div style={{ fontSize: '0.9rem' }}>
                                        {this.state.infoLoading && 'Loading...'}
                                        {!this.state.infoLoading && (
                                            this.getFirstLineOfDescription(this.state.eventDetailsCache[this.state.mobileModal.event.eventId]) || 'Event details available on detail page.'
                                        )}
                                        <div style={{ marginTop: 8 }}>
                                            <Link to={`/event_details/${this.state.mobileModal.event.eventId}`} onClick={() => this.closeMobileModal()} style={{ color: '#007bff', textDecoration: 'underline' }}>View full details</Link>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <Footer />
                
                {/* Location Popup */}
                {
                locationPopup.show && (
                    <div 
                        className="location-popup"
                        style={{
                            position: 'fixed',
                            top: `${locationPopup.position.top}px`,
                            left: `${locationPopup.position.left}px`,
                            '--arrow-left': `${locationPopup.position.arrowLeft || 110}px`
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={this.handleLocationPopupEnter}
                        onMouseLeave={this.handleLocationPopupLeave}
                    >
                        <div style={{ marginBottom: '6px' }}>
                            <strong>Address:</strong>
                        </div>
                        <div style={{ marginBottom: '8px', wordWrap: 'break-word' }}>
                            {locationPopup.address || 'Address not available'}
                        </div>
                        {locationPopup.googleMapsUrl ? (
                            <div>
                                <a 
                                    href={locationPopup.googleMapsUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ 
                                        color: '#007bff', 
                                        textDecoration: 'underline',
                                        wordBreak: 'break-all',
                                        fontSize: '0.65rem'
                                    }}
                                >
                                    {locationPopup.googleMapsUrl}
                                </a>
                            </div>
                        ) : (
                            <div style={{ fontSize: '0.65rem', color: '#6c757d', fontStyle: 'italic' }}>
                                Google Maps link not available
                            </div>
                        )}
                    </div>
                )}
                {/* event eliibility popup */}
                {this.state.eligibilityPopup.show && this.state.eligibilityPopup.event && (
                    <div
                        className="location-popup"
                        style={{
                            position: 'fixed',
                            top: `${this.state.eligibilityPopup.position.top}px`,
                            left: `${this.state.eligibilityPopup.position.left}px`
                        }}
                        onMouseEnter={(e) => e.stopPropagation()}
                        onMouseLeave={() =>
                            this.setState({
                                eligibilityPopup: { show: false, event: null, position: { top: 0, left: 0 } }
                            })
                        }
                        onClick={(e) => e.stopPropagation()}
                    >
                        <strong>Eligibility Criteria</strong>
                        <div style={{ fontSize: '0.8rem', marginTop: '6px' }}>
                            <Link
                                to={`/event_details/${this.state.eligibilityPopup.event.eventId}`}
                                style={{ color: '#007bff', textDecoration: 'underline' }}
                            >
                                [Check eligibility]
                            </Link>
                            &nbsp;of the course on detail page.
                        </div>
                    </div>
                )}
                {/* ✅ EVENT INFORMATION POPUP */}
                {this.state.infoPopup.show && this.state.infoPopup.event && (
                    <div
                        className="location-popup"
                        style={{
                            position: 'fixed',
                            top: `${this.state.infoPopup.position.top}px`,
                            left: `${this.state.infoPopup.position.left}px`
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <strong>Event Information</strong>
                        <div style={{ fontSize: '0.8rem', marginTop: '6px' }}>
                           {this.state.infoLoading && 'Loading...'}
                            {!this.state.infoLoading &&
                                (this.getFirstLineOfDescription(
                                    this.state.eventDetailsCache[this.state.infoPopup.event.eventId]
                                ) || 'Event details available on detail page.')
                            }
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default withRouter(translate(EventsCalendarPage));

